import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useSchool } from '../contexts/SchoolContext';
import { useUser } from '../contexts/UserContext';
import { useUrl } from '../contexts/UrlContext';
import { getAdminUrl } from '../utils/auth';
import { useSelector } from 'react-redux';
import { getAdminUrl as urlUtilsGetAdminUrl } from '../utils/urlUtils';

// Custom event for form changes
export const formChangedEvent = new CustomEvent('formChanged', { 
  detail: { hasChanges: false }
});

const Navbar = ({ homeUrl = '/', schoolName = null, homepageUrl = null, isAdmin = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { school } = useSchool();
  const { user, logout } = useUser();
  const { getAdminUrl, getPublicSiteUrl } = useUrl();
  
  // Get branding colors from Redux
  const branding = useSelector(state => state.branding);
  // Use the timestamp to force re-renders
  // eslint-disable-next-line no-unused-vars
  const updateTimestamp = useSelector(state => state.branding.updateTimestamp);
  
  // Get color values with fallbacks
  const primaryBgColor = branding?.primaryBgColor || '#4A90E2';
  const primaryTextColor = branding?.primaryTextColor || '#FFFFFF';
  
  // Get the admin base URL that includes the subdomain if needed
  const adminUrl = getAdminUrl();
  const previewUrl = `${adminUrl}/preview`;
  
  // Check user roles for permission-based UI
  const userRolesForSchool = user?.schools
    ?.find(s => s.id === user?.current_school?.id)?.roles || [];
  const hasAdminRole = userRolesForSchool.includes('Admin');
  const isSuperadmin = userRolesForSchool.includes('Superadmin');
  const isEditor = userRolesForSchool.includes('Editor');
  const isViewer = userRolesForSchool.includes('Viewer') && 
                  !isEditor && !hasAdminRole && !isSuperadmin;
  
  // Only Editor, Admin, Superadmin can edit menus
  const canEditMenus = isEditor || hasAdminRole || isSuperadmin;
  
  // Helper function to check if a link is active (for admin navigation)
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  // Check if we're on the school selector dashboard
  const isSchoolSelector = location.pathname === '/dashboard';
  
  const isLoggedIn = localStorage.getItem('accessToken') !== null;
  const isMenuEditor = location.pathname.includes('/admin/menu-editor');
  const isPreview = location.pathname.includes('/admin/preview');
  
  // Get the public site URL with subdomain preserved
  const getPublicSiteUrlFromContext = () => {
    return getPublicSiteUrl();
  };
  
  // Listen for form changes
  useEffect(() => {
    const handleFormChanged = (e) => {
      setHasUnsavedChanges(e.detail.hasChanges);
    };

    window.addEventListener('formChanged', handleFormChanged);
    
    return () => {
      window.removeEventListener('formChanged', handleFormChanged);
    };
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleDashboardClick = (e) => {
    if (isMenuEditor && hasUnsavedChanges) {
      e.preventDefault();
      setShowConfirmation(true);
    }
  };

  // Common button styling for consistency
  const buttonStyle = {
    backgroundColor: primaryTextColor,
    color: primaryBgColor
  };
  
  return (
    <nav style={{ backgroundColor: primaryBgColor, color: primaryTextColor }} className="p-4 shadow-md">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        {/* Left side - Logo/Title */}
        <div className="flex items-center">
          {isAdmin && school ? (
            // Admin mode with school selected
            <div className="flex items-center">
              <h1 className="text-xl font-bold">
                {isViewer ? (
                  `${school.name} ${t('preview')}`
                ) : (
                  `${school.name} ${t('')}`
                )}
              </h1>
            </div>
          ) : isAdmin ? (
            // Admin mode without school selected
            <h1 className="text-xl font-bold">
              {t('adminDashboard')}
            </h1>
          ) : (
            // Regular mode
            <Link to={homeUrl} style={{ color: primaryTextColor }} className="text-xl font-bold">
              {schoolName ? schoolName : t('welcome')}
            </Link>
          )}
          
          {!isAdmin && homepageUrl && (
            <a 
              href={homepageUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: primaryTextColor }}
              className="ml-4 text-sm underline hover:opacity-80"
            >
              {t('schoolHomepage')}
            </a>
          )}
        </div>
        
        {/* Right side - All buttons aligned to the right */}
        <div className="flex items-center space-x-3">
          {/* Dashboard button (Admin) - Only for non-viewers and not on dashboard page */}
          {isAdmin && !isViewer && !isSchoolSelector && (
            <Link
              to={adminUrl}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                isActive(adminUrl) 
                  ? 'bg-opacity-80' 
                  : ''
              }`}
              style={buttonStyle}
            >
              {t('dashboard')}
            </Link>
          )}
          
          {/* View Public Site button (Admin) - Only if we have a school context */}
          {isAdmin && school && (
            <Link
              to={getPublicSiteUrlFromContext()}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={buttonStyle}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('viewPublicSite')}
            </Link>
          )}
          
          {/* All Schools button (Admin) - Show if admin and not on school selector dashboard */}
          {isAdmin && !isSchoolSelector && (
            <Link
              to="/dashboard"
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={buttonStyle}
            >
              {t('allSchools')}
            </Link>
          )}
          
          {/* Language Selector */}
          <LanguageSelector />
          
          {/* Dashboard button (Non-Admin) - Only if can edit menus */}
          {isLoggedIn && !isAdmin && canEditMenus && (
            <Link
              to="/admin"
              onClick={handleDashboardClick}
              style={buttonStyle}
              className="px-4 py-2 rounded-md text-sm font-medium"
            >
              {t('dashboard')}
            </Link>
          )}
          
          {/* Login/Logout button */}
          {isLoggedIn ? (
            <>
              <Link
                to="/profile"
                style={buttonStyle}
                className="px-4 py-2 rounded-md text-sm font-medium"
              >
                {t('profile', 'Profile')}
              </Link>
              <button
                onClick={handleLogout}
                style={buttonStyle}
                className="px-4 py-2 rounded-md text-sm font-medium"
              >
                {t('logout')}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              style={buttonStyle}
              className="px-4 py-2 rounded-md text-sm font-medium"
            >
              {t('login')}
            </Link>
          )}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('unsavedChanges', 'You have unsaved changes')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('unsavedChangesMessage', 'If you leave this page, any unsaved changes will be lost.')}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  navigate('/admin');
                }}
                className="px-4 py-2 bg-red-600 rounded-md text-white hover:bg-red-700"
              >
                {t('leaveAnyway', 'Leave anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 